import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminAxios, getOrderPin, setOrderPin, formatPence, API } from './ordering/orderingApi';
import usePageTitle from '@/hooks/usePageTitle';

const emptyChoice = () => ({ id: crypto.randomUUID(), name: '', price_delta_pence: 0 });
const emptyGroup = () => ({ id: crypto.randomUUID(), name: '', required: false, choices: [emptyChoice()] });

export default function OrderSettingsPage() {
  usePageTitle('Order Settings');
  const [authed, setAuthed] = useState(!!getOrderPin());
  const [pinInput, setPinInput] = useState('');

  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelForm, setLabelForm] = useState({ logo_url: '', title_text: '', prefix: '', start_number: 1, finish_number: 50, format: 'thermal' });
  const [labelLoading, setLabelLoading] = useState(false);

  // Local edit drafts to avoid trampling user typing
  const [editingProduct, setEditingProduct] = useState(null);

  const [seatCount, setSeatCount] = useState(0);

  const reload = async () => {
    const [s, c, p, seats] = await Promise.all([
      adminAxios().get('/admin/order/settings'),
      adminAxios().get('/admin/order/categories'),
      adminAxios().get('/admin/order/products'),
      adminAxios().get('/admin/order/seats'),
    ]);
    setSettings(s.data);
    setCategories(c.data.categories || []);
    setProducts(p.data.products || []);
    setSeatCount(seats.data.count || 0);
  };

  useEffect(() => {
    if (!authed) return;
    reload().catch((err) => {
      if (err.response?.status === 401) { setOrderPin(''); setAuthed(false); }
    });
  }, [authed]);

  const saveSettings = async (patch) => {
    try {
      const res = await adminAxios().put('/admin/order/settings', patch);
      setSettings(res.data);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const updateLocal = (path, value) => {
    setSettings((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const segs = path.split('.');
      let target = next;
      for (let i = 0; i < segs.length - 1; i += 1) target = target[segs[i]];
      target[segs[segs.length - 1]] = value;
      return next;
    });
  };

  const addCategory = async () => {
    const name = window.prompt('New category name?');
    if (!name) return;
    await adminAxios().post('/admin/order/categories', { name, position: categories.length });
    await reload();
  };

  const renameCategory = async (cat) => {
    const name = window.prompt('Rename category', cat.name);
    if (!name) return;
    await adminAxios().put(`/admin/order/categories/${cat.id}`, { name, position: cat.position });
    await reload();
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}" and all its products?`)) return;
    await adminAxios().delete(`/admin/order/categories/${cat.id}`);
    await reload();
  };

  const startNewProduct = (categoryId) => setEditingProduct({
    isNew: true,
    category_id: categoryId,
    name: '',
    price_pence: 0,
    position: 0,
    options: [],
  });

  const editProduct = (p) => setEditingProduct({ ...p, options: p.options || [] });

  const saveProduct = async () => {
    const p = editingProduct;
    if (!p.name?.trim()) return toast.error('Name required');
    try {
      if (p.isNew) {
        await adminAxios().post('/admin/order/products', {
          category_id: p.category_id,
          name: p.name.trim(),
          price_pence: Math.round(p.price_pence),
          position: p.position || 0,
          options: p.options || [],
        });
      } else {
        await adminAxios().put(`/admin/order/products/${p.id}`, {
          category_id: p.category_id,
          name: p.name.trim(),
          price_pence: Math.round(p.price_pence),
          position: p.position || 0,
          options: p.options || [],
        });
      }
      setEditingProduct(null);
      await reload();
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await adminAxios().delete(`/admin/order/products/${p.id}`);
    await reload();
  };

  const downloadLabels = async () => {
    setLabelLoading(true);
    try {
      const res = await adminAxios().post('/admin/order/seat-labels', labelForm, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'seat-labels.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      setLabelOpen(false);
      await reload();
    } catch (err) {
      let msg = 'Failed';
      try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch { /* noop */ }
      toast.error(msg);
    } finally {
      setLabelLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <form onSubmit={(e) => { e.preventDefault(); setOrderPin(pinInput); setAuthed(true); }} className="bg-white rounded-2xl p-8 shadow w-80 space-y-3">
          <h1 className="font-bold text-xl">Order Settings</h1>
          <input type="password" value={pinInput} onChange={(e) => setPinInput(e.target.value)} placeholder="Admin PIN" className="w-full px-3 py-2 border rounded-lg" />
          <button className="w-full py-2 bg-black text-white rounded-lg font-semibold">Enter</button>
        </form>
      </div>
    );
  }

  if (!settings) return <div className="p-10 text-center">Loading…</div>;

  const branding = settings.order_page_branding || {};
  const sumup = settings.sumup_settings || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <h1 className="font-bold text-xl">Order Settings</h1>
        <button onClick={() => { setOrderPin(''); setAuthed(false); }} className="text-xs underline opacity-70">Logout</button>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4 space-y-6">

        {/* Master toggles */}
        <section className="bg-white rounded-xl p-4 shadow-sm space-y-3">
          <h2 className="font-bold">Master toggles</h2>
          {[
            { key: 'online_ordering_disabled', label: 'Online ordering is DISABLED', invert: true },
            { key: 'raffle_upsell_disabled', label: 'Raffle upsell is DISABLED', invert: true },
            { key: 'seat_number_mode_enabled', label: 'Seat-number ordering mode' },
            { key: 'donations_enabled', label: 'Donation appeal pop-up after checkout' },
          ].map((t) => (
            <label key={t.key} className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={!!settings[t.key]}
                onChange={(e) => saveSettings({ [t.key]: e.target.checked })}
                data-testid={`toggle-${t.key}`}
              />
              <span>{t.label}</span>
            </label>
          ))}
          <div className="flex gap-2">
            <button onClick={async () => { const r = await adminAxios().post('/admin/reconcile-payments'); toast.success(`Reconciled ${r.data.reconciled}/${r.data.checked}`); }} className="bg-black text-white px-3 py-1.5 rounded text-sm" data-testid="reconcile-btn">
              Recover paid orders
            </button>
            <button onClick={() => setLabelOpen(true)} className="bg-amber-500 text-black px-3 py-1.5 rounded text-sm font-semibold" data-testid="print-labels-btn">
              Print Seat Numbers
            </button>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-600 mt-1 border-t pt-2">
            <span data-testid="registered-seats-count">
              <strong>{seatCount}</strong> seat{seatCount === 1 ? '' : 's'} registered for ordering
              {seatCount === 0 && ' (any seat number accepted until you generate labels)'}
            </span>
            {seatCount > 0 && (
              <button
                onClick={async () => {
                  if (!window.confirm('Remove all registered seats? Customers will be able to enter any seat until you generate new labels.')) return;
                  await adminAxios().delete('/admin/order/seats');
                  await reload();
                  toast.success('Seat registry cleared');
                }}
                className="text-red-600 underline"
                data-testid="clear-seats-btn"
              >
                Clear seat registry
              </button>
            )}
          </div>
        </section>

        {/* Donation appeal */}
        {settings.donations_enabled && (
          <section className="bg-white rounded-xl p-4 shadow-sm space-y-3" data-testid="donation-settings-section">
            <h2 className="font-bold">Donation appeal</h2>
            <p className="text-xs text-gray-500">Shown to customers after they complete their basket and click pay, before the SumUp checkout.</p>
            <label className="block text-xs font-semibold">Appeal message</label>
            <textarea
              value={settings.donation_message || ''}
              onChange={(e) => setSettings({ ...settings, donation_message: e.target.value })}
              onBlur={(e) => saveSettings({ donation_message: e.target.value })}
              placeholder="Enjoying the event? Please consider adding a small donation to support the club."
              className="w-full border rounded-lg px-3 py-2 text-sm"
              rows={3}
              maxLength={500}
              data-testid="donation-message-input"
            />
            <p className="text-xs text-gray-400">{(settings.donation_message || '').length}/500</p>
          </section>
        )}

        {/* SumUp creds */}
        <section className="bg-white rounded-xl p-4 shadow-sm space-y-3">
          <h2 className="font-bold">SumUp credentials</h2>
          <p className="text-xs text-gray-500">Stored server-side. Never sent to customer browsers.</p>
          {['api_key', 'merchant_code', 'public_base_url'].map((k) => (
            <div key={k}>
              <label className="text-xs font-semibold uppercase">{k.replace(/_/g, ' ')}</label>
              <input
                value={sumup[k] || ''}
                onChange={(e) => updateLocal(`sumup_settings.${k}`, e.target.value)}
                className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                placeholder={k === 'public_base_url' ? 'https://scorz.co.uk' : ''}
                data-testid={`sumup-${k}`}
              />
            </div>
          ))}
          <button onClick={() => saveSettings({ sumup_settings: settings.sumup_settings })} className="bg-black text-white px-3 py-1.5 rounded text-sm" data-testid="save-sumup-btn">Save SumUp settings</button>
        </section>

        {/* Branding */}
        <section className="bg-white rounded-xl p-4 shadow-sm space-y-3">
          <h2 className="font-bold">Public Order Page Design</h2>
          <div>
            <label className="text-xs font-semibold uppercase">Logo URL</label>
            <input value={branding.logo_url || ''} onChange={(e) => updateLocal('order_page_branding.logo_url', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="https://…/logo.png" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              ['background_color', 'Background'],
              ['text_color', 'Text'],
              ['button_color', 'Button'],
              ['button_text_color', 'Button text'],
              ['accent_color', 'Accent'],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs font-semibold">{label}</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={branding[k] || '#000000'} onChange={(e) => updateLocal(`order_page_branding.${k}`, e.target.value)} className="w-10 h-10 border rounded" />
                  <input value={branding[k] || ''} onChange={(e) => updateLocal(`order_page_branding.${k}`, e.target.value)} className="flex-1 px-2 py-1 border rounded font-mono text-xs" />
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => saveSettings({ order_page_branding: settings.order_page_branding })} className="bg-black text-white px-3 py-1.5 rounded text-sm" data-testid="save-branding-btn">Save branding</button>
        </section>

        {/* Raffle SMS template */}
        <section className="bg-white rounded-xl p-4 shadow-sm space-y-3">
          <h2 className="font-bold">Raffle SMS template</h2>
          <p className="text-xs text-gray-500">Supports <code>{'{first_name}'}</code>. Empty resets to default. Max 320 chars.</p>
          <textarea
            value={settings.raffle_sms_template || ''}
            onChange={(e) => updateLocal('raffle_sms_template', e.target.value)}
            rows={3}
            maxLength={320}
            className="w-full px-3 py-2 border rounded-lg text-sm"
            data-testid="sms-template-input"
          />
          <button onClick={() => saveSettings({ raffle_sms_template: settings.raffle_sms_template })} className="bg-black text-white px-3 py-1.5 rounded text-sm" data-testid="save-sms-btn">Save SMS template</button>
        </section>

        {/* Menu management */}
        <section className="bg-white rounded-xl p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold">Menu</h2>
            <button onClick={addCategory} className="text-sm bg-black text-white px-3 py-1.5 rounded" data-testid="add-category-btn">+ Category</button>
          </div>
          {categories.length === 0 && <p className="text-sm text-gray-500">No categories yet.</p>}
          {categories.map((cat) => (
            <div key={cat.id} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="font-semibold">{cat.name}</div>
                <div className="flex gap-2">
                  <button onClick={() => renameCategory(cat)} className="text-xs px-2 py-1 border rounded">Rename</button>
                  <button onClick={() => deleteCategory(cat)} className="text-xs px-2 py-1 border rounded text-red-600">Delete</button>
                  <button onClick={() => startNewProduct(cat.id)} className="text-xs bg-black text-white px-2 py-1 rounded" data-testid={`add-product-${cat.id}`}>+ Product</button>
                </div>
              </div>
              <div className="space-y-1">
                {products.filter((p) => p.category_id === cat.id).map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1.5 rounded">
                    <span>{p.name} — {formatPence(p.price_pence)}</span>
                    <div className="flex gap-2">
                      <button onClick={() => editProduct(p)} className="text-xs px-2 py-0.5 border rounded">Edit</button>
                      <button onClick={() => deleteProduct(p)} className="text-xs px-2 py-0.5 border rounded text-red-600">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      </main>

      {/* Product editor modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-lg mb-3">{editingProduct.isNew ? 'New product' : 'Edit product'}</h3>
            <div className="space-y-2">
              <input value={editingProduct.name} onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })} placeholder="Name" className="w-full px-3 py-2 border rounded-lg" />
              <label className="text-xs">Price (£)</label>
              <input type="number" step="0.01" value={editingProduct.price_pence / 100} onChange={(e) => setEditingProduct({ ...editingProduct, price_pence: Math.round((parseFloat(e.target.value) || 0) * 100) })} className="w-full px-3 py-2 border rounded-lg" />

              <div className="flex items-center justify-between mt-4">
                <h4 className="font-semibold">Option groups</h4>
                <button onClick={() => setEditingProduct({ ...editingProduct, options: [...editingProduct.options, emptyGroup()] })} className="text-xs border px-2 py-1 rounded">+ Group</button>
              </div>
              {editingProduct.options.map((g, gi) => (
                <div key={g.id} className="border rounded-lg p-2 space-y-2 bg-gray-50">
                  <div className="flex gap-2 items-center">
                    <input value={g.name} onChange={(e) => {
                      const opts = [...editingProduct.options];
                      opts[gi] = { ...opts[gi], name: e.target.value };
                      setEditingProduct({ ...editingProduct, options: opts });
                    }} placeholder="Group name (e.g. Flavour)" className="flex-1 px-2 py-1 border rounded" />
                    <label className="text-xs flex items-center gap-1">
                      <input type="checkbox" checked={g.required} onChange={(e) => {
                        const opts = [...editingProduct.options];
                        opts[gi] = { ...opts[gi], required: e.target.checked };
                        setEditingProduct({ ...editingProduct, options: opts });
                      }} />
                      required
                    </label>
                    <button onClick={() => setEditingProduct({ ...editingProduct, options: editingProduct.options.filter((_, i) => i !== gi) })} className="text-xs text-red-600">×</button>
                  </div>
                  {g.choices.map((c, ci) => (
                    <div key={c.id} className="flex gap-2 items-center">
                      <input value={c.name} onChange={(e) => {
                        const opts = [...editingProduct.options];
                        const choices = [...opts[gi].choices];
                        choices[ci] = { ...choices[ci], name: e.target.value };
                        opts[gi] = { ...opts[gi], choices };
                        setEditingProduct({ ...editingProduct, options: opts });
                      }} placeholder="Choice name" className="flex-1 px-2 py-1 border rounded text-sm" />
                      <input type="number" step="0.01" value={c.price_delta_pence / 100} onChange={(e) => {
                        const opts = [...editingProduct.options];
                        const choices = [...opts[gi].choices];
                        choices[ci] = { ...choices[ci], price_delta_pence: Math.round((parseFloat(e.target.value) || 0) * 100) };
                        opts[gi] = { ...opts[gi], choices };
                        setEditingProduct({ ...editingProduct, options: opts });
                      }} placeholder="+£" className="w-20 px-2 py-1 border rounded text-sm" />
                      <button onClick={() => {
                        const opts = [...editingProduct.options];
                        opts[gi] = { ...opts[gi], choices: opts[gi].choices.filter((_, i) => i !== ci) };
                        setEditingProduct({ ...editingProduct, options: opts });
                      }} className="text-xs text-red-600">×</button>
                    </div>
                  ))}
                  <button onClick={() => {
                    const opts = [...editingProduct.options];
                    opts[gi] = { ...opts[gi], choices: [...opts[gi].choices, emptyChoice()] };
                    setEditingProduct({ ...editingProduct, options: opts });
                  }} className="text-xs border px-2 py-1 rounded">+ Choice</button>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditingProduct(null)} className="px-3 py-1.5 border rounded">Cancel</button>
              <button onClick={saveProduct} className="px-3 py-1.5 bg-black text-white rounded" data-testid="save-product-btn">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Seat labels modal */}
      {labelOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-md">
            <h3 className="font-bold text-lg mb-3">Print Seat Numbers</h3>
            <div className="space-y-2">
              <input value={labelForm.logo_url} onChange={(e) => setLabelForm({ ...labelForm, logo_url: e.target.value })} placeholder="Logo URL (optional)" className="w-full px-3 py-2 border rounded" />
              <input value={labelForm.title_text} onChange={(e) => setLabelForm({ ...labelForm, title_text: e.target.value })} placeholder="Event title (optional)" className="w-full px-3 py-2 border rounded" />
              <div className="grid grid-cols-3 gap-2">
                <input value={labelForm.prefix} onChange={(e) => setLabelForm({ ...labelForm, prefix: e.target.value.toUpperCase() })} placeholder="Prefix" className="px-3 py-2 border rounded" />
                <input type="number" value={labelForm.start_number} onChange={(e) => setLabelForm({ ...labelForm, start_number: parseInt(e.target.value || '0', 10) })} placeholder="Start" className="px-3 py-2 border rounded" />
                <input type="number" value={labelForm.finish_number} onChange={(e) => setLabelForm({ ...labelForm, finish_number: parseInt(e.target.value || '0', 10) })} placeholder="Finish" className="px-3 py-2 border rounded" />
              </div>
              <p className="text-xs text-gray-500">
                Generates <strong>{Math.max(0, labelForm.finish_number - labelForm.start_number + 1)}</strong> labels (76 × 25 mm each)
              </p>

              <div className="pt-2">
                <div className="text-xs font-semibold text-gray-700 mb-1">Print Format</div>
                <div className="space-y-1">
                  <label className="flex items-start gap-2 cursor-pointer p-2 border rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      name="label-format"
                      value="thermal"
                      checked={labelForm.format === 'thermal'}
                      onChange={() => setLabelForm({ ...labelForm, format: 'thermal' })}
                      className="mt-0.5"
                      data-testid="label-format-thermal"
                    />
                    <span className="text-sm">
                      <strong>Label printer</strong> — one 76 × 25 mm label per page (for thermal label printers)
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer p-2 border rounded hover:bg-gray-50">
                    <input
                      type="radio"
                      name="label-format"
                      value="a4"
                      checked={labelForm.format === 'a4'}
                      onChange={() => setLabelForm({ ...labelForm, format: 'a4' })}
                      className="mt-0.5"
                      data-testid="label-format-a4"
                    />
                    <span className="text-sm">
                      <strong>A4 sheet</strong> — 20 labels per page (2 × 10) with cut guides for scissors
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setLabelOpen(false)} className="px-3 py-1.5 border rounded">Cancel</button>
              <button onClick={downloadLabels} disabled={labelLoading} className="px-3 py-1.5 bg-black text-white rounded disabled:opacity-50" data-testid="generate-labels-btn">
                {labelLoading ? 'Generating…' : 'Generate PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
